import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { COLORS } from '../../constants/colors';
import { setContrast, useBrandColors, safeRemoveNode } from '@charitystack/shared';
import { toast } from 'sonner';
import { createRoot } from 'react-dom/client';

const QRCodeModal = ({
  open,
  setOpen,
  url,
  title = 'QR Code',
  label = '',
  instructionText = '',
  bannerColor = COLORS.FORM_DEFAULT_COLOR || '#ffffff',
}) => {
  const qrRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);

  // Use shared brand colors hook to generate color palette
  const { brandColors, primaryColor } = useBrandColors(bannerColor);

  const copyToClipboard = async () => {
    try {
      setIsCopying(true);
      const effectiveColor = brandColors['accent-50'] || primaryColor;

      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      document.body.appendChild(tempContainer);

      const root = createRoot(tempContainer);
      let qrRef = null;

      root.render(
        <div
          ref={(ref) => {
            qrRef = ref;
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            border: '1px solid #E0D9CC',
            backgroundColor: '#ffffff',
            padding: '8px',
          }}
        >
          <div style={{ paddingTop: '24px', paddingBottom: '40px', display: 'inline-block' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <QRCodeSVG
                value={url}
                size={200}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                includeMargin={false}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-36px',
                  left: 0,
                  right: 0,
                  borderRadius: '0 0 8px 8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  paddingBottom: '16px',
                  backgroundColor: effectiveColor,
                }}
              >
                <p
                  style={{
                    wordBreak: 'break-words',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    color: setContrast(effectiveColor),
                    margin: 0,
                  }}
                >
                  {label || 'Donate Now'}
                </p>
              </div>
            </div>
          </div>
        </div>,
      );

      // Wait for rendering
      setTimeout(async () => {
        if (qrRef) {
          try {
            window.focus();
            const canvas = await html2canvas(qrRef, {
              backgroundColor: '#ffffff',
              scale: 3,
              useCORS: true,
              allowTaint: false,
            });

            canvas.toBlob(async (blob) => {
              if (!blob) {
                setIsCopying(false);
                toast.error('Failed to generate image');
                root.unmount();
                safeRemoveNode(tempContainer);
                return;
              }

              try {
                // Check if Clipboard API is available
                if (!navigator?.clipboard?.write) {
                  toast.error('Clipboard API not available in this context');
                  setIsCopying(false);
                  root.unmount();
                  safeRemoveNode(tempContainer);
                  return;
                }
                
                await navigator.clipboard.write([
                  new ClipboardItem({
                    'image/png': blob,
                  }),
                ]);
                setIsCopying(false);
                toast.success('QR Code image copied to clipboard!');
              } catch (err) {
                console.error('Failed to copy image: ', err);
                setIsCopying(false);
                toast.error('Failed to copy image. Please try downloading instead.');
              } finally {
                root.unmount();
                safeRemoveNode(tempContainer);
              }
            }, 'image/png');
          } catch (err) {
            console.error('Failed to generate image: ', err);
            setIsCopying(false);
            toast.error('Failed to generate QR code image');
            root.unmount();
            safeRemoveNode(tempContainer);
          }
        }
      }, 200);
    } catch (err) {
      console.error('Failed to initialize QR code: ', err);
      setIsCopying(false);
      toast.error('Failed to generate QR Code image');
    }
  };

  const downloadImage = async () => {
    try {
      setIsDownloading(true);
      const effectiveColor = brandColors['accent-50'] || primaryColor;

      // Create a temporary container
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      document.body.appendChild(tempContainer);

      const root = createRoot(tempContainer);
      let qrRef = null;

      root.render(
        <div
          ref={(ref) => {
            qrRef = ref;
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            border: '1px solid #E0D9CC',
            backgroundColor: '#ffffff',
            padding: '8px',
          }}
        >
          <div style={{ paddingTop: '24px', paddingBottom: '40px', display: 'inline-block' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <QRCodeSVG
                value={url}
                size={200}
                bgColor="#ffffff"
                fgColor="#000000"
                level="M"
                includeMargin={false}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-36px',
                  left: 0,
                  right: 0,
                  borderRadius: '0 0 8px 8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  paddingBottom: '16px',
                  backgroundColor: effectiveColor,
                }}
              >
                <p
                  style={{
                    wordBreak: 'break-words',
                    fontSize: '14px',
                    fontWeight: '600',
                    textAlign: 'center',
                    color: setContrast(effectiveColor),
                    margin: 0,
                  }}
                >
                  {label || 'Donate Now'}
                </p>
              </div>
            </div>
          </div>
        </div>,
      );

      // Wait for rendering
      setTimeout(async () => {
        if (qrRef) {
          try {
            const canvas = await html2canvas(qrRef, {
              backgroundColor: '#ffffff',
              scale: 3,
              useCORS: true,
              allowTaint: false,
            });

            const link = document.createElement('a');
            const baseFilename = label
              ? label
                  .toLowerCase()
                  .replace(/[^a-z0-9]/g, '-')
                  .replace(/-+/g, '-')
                  .substring(0, 30)
              : title.toLowerCase().replace(/\s+/g, '-');
            link.download = `${baseFilename}-qr-code.png`;
            link.href = canvas.toDataURL();
            link.click();

            setIsDownloading(false);
            toast.success('QR Code downloaded successfully!');
            root.unmount();
            safeRemoveNode(tempContainer);
          } catch (err) {
            console.error('Failed to download QR code: ', err);
            setIsDownloading(false);
            toast.error('Failed to download QR Code');
            root.unmount();
            safeRemoveNode(tempContainer);
          }
        }
      }, 200);
    } catch (err) {
      console.error('Failed to initialize QR code: ', err);
      setIsDownloading(false);
      toast.error('Failed to generate QR Code image');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" showX={false}>
        <DialogHeader>
          <div className="flex gap-2">
            <div className="flex h-[38px] w-[50px] sm:w-[38px] items-center justify-center rounded-[19px] border border-edge bg-paper-inset">
              <svg
                width="18"
                height="19"
                viewBox="0 0 14 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M1.75 2.98126C1.75 2.3772 2.23969 1.88751 2.84375 1.88751H5.46875C6.07281 1.88751 6.5625 2.3772 6.5625 2.98126V5.60626C6.5625 6.21032 6.07281 6.70001 5.46875 6.70001H2.84375C2.23969 6.70001 1.75 6.21032 1.75 5.60626V2.98126ZM2.84375 2.76251C2.72294 2.76251 2.625 2.86045 2.625 2.98126V5.60626C2.625 5.72707 2.72294 5.82501 2.84375 5.82501H5.46875C5.58956 5.82501 5.6875 5.72707 5.6875 5.60626V2.98126C5.6875 2.86045 5.58956 2.76251 5.46875 2.76251H2.84375ZM7.4375 2.98126C7.4375 2.3772 7.92719 1.88751 8.53125 1.88751H11.1562C11.7603 1.88751 12.25 2.3772 12.25 2.98126V5.60626C12.25 6.21032 11.7603 6.70001 11.1562 6.70001H8.53125C7.92719 6.70001 7.4375 6.21032 7.4375 5.60626V2.98126ZM8.53125 2.76251C8.41044 2.76251 8.3125 2.86045 8.3125 2.98126V5.60626C8.3125 5.72707 8.41044 5.82501 8.53125 5.82501H11.1562C11.2771 5.82501 11.375 5.72707 11.375 5.60626V2.98126C11.375 2.86045 11.2771 2.76251 11.1562 2.76251H8.53125ZM3.5 4.07501C3.5 3.83339 3.69588 3.63751 3.9375 3.63751H4.375C4.61662 3.63751 4.8125 3.83339 4.8125 4.07501V4.51251C4.8125 4.75414 4.61662 4.95001 4.375 4.95001H3.9375C3.69588 4.95001 3.5 4.75414 3.5 4.51251V4.07501ZM9.1875 4.07501C9.1875 3.83339 9.38338 3.63751 9.625 3.63751H10.0625C10.3041 3.63751 10.5 3.83339 10.5 4.07501V4.51251C10.5 4.75414 10.3041 4.95001 10.0625 4.95001H9.625C9.38338 4.95001 9.1875 4.75414 9.1875 4.51251V4.07501ZM1.75 8.66876C1.75 8.0647 2.23969 7.57501 2.84375 7.57501H5.46875C6.07281 7.57501 6.5625 8.0647 6.5625 8.66876V11.2938C6.5625 11.8978 6.07281 12.3875 5.46875 12.3875H2.84375C2.23969 12.3875 1.75 11.8978 1.75 11.2938V8.66876ZM2.84375 8.45001C2.72294 8.45001 2.625 8.54795 2.625 8.66876V11.2938C2.625 11.4146 2.72294 11.5125 2.84375 11.5125H5.46875C5.58956 11.5125 5.6875 11.4146 5.6875 11.2938V8.66876C5.6875 8.54795 5.58956 8.45001 5.46875 8.45001H2.84375ZM7.4375 8.01251C7.4375 7.77089 7.63338 7.57501 7.875 7.57501H8.3125C8.55412 7.57501 8.75 7.77089 8.75 8.01251V8.45001C8.75 8.69164 8.55412 8.88751 8.3125 8.88751H7.875C7.63338 8.88751 7.4375 8.69164 7.4375 8.45001V8.01251ZM10.9375 8.01251C10.9375 7.77089 11.1334 7.57501 11.375 7.57501H11.8125C12.0541 7.57501 12.25 7.77089 12.25 8.01251V8.45001C12.25 8.69164 12.0541 8.88751 11.8125 8.88751H11.375C11.1334 8.88751 10.9375 8.69164 10.9375 8.45001V8.01251ZM3.5 9.76251C3.5 9.52089 3.69588 9.32501 3.9375 9.32501H4.375C4.61662 9.32501 4.8125 9.52089 4.8125 9.76251V10.2C4.8125 10.4416 4.61662 10.6375 4.375 10.6375H3.9375C3.69588 10.6375 3.5 10.4416 3.5 10.2V9.76251ZM9.1875 9.76251C9.1875 9.52089 9.38338 9.32501 9.625 9.32501H10.0625C10.3041 9.32501 10.5 9.52089 10.5 9.76251V10.2C10.5 10.4416 10.3041 10.6375 10.0625 10.6375H9.625C9.38338 10.6375 9.1875 10.4416 9.1875 10.2V9.76251ZM7.4375 11.5125C7.4375 11.2709 7.63338 11.075 7.875 11.075H8.3125C8.55412 11.075 8.75 11.2709 8.75 11.5125V11.95C8.75 12.1916 8.55412 12.3875 8.3125 12.3875H7.875C7.63338 12.3875 7.4375 12.1916 7.4375 11.95V11.5125ZM10.9375 11.5125C10.9375 11.2709 11.1334 11.075 11.375 11.075H11.8125C12.0541 11.075 12.25 11.2709 12.25 11.5125V11.95C12.25 12.1916 12.0541 12.3875 11.8125 12.3875H11.375C11.1334 12.3875 10.9375 12.1916 10.9375 11.95V11.5125Z"
                  fill="#3A3429"
                />
              </svg>
            </div>
            <div className="flex flex-col items-start">
              <DialogTitle className="text-md leading-6 text-ink-muted">{title}</DialogTitle>
              <p className="text-sm leading-6 text-ink-muted text-left">{instructionText}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-2 text-center">
          {/* QR Code with Label - This entire div will be downloaded */}
          <div
            ref={qrRef}
            className="flex w-full flex-col items-center border justify-center rounded-lg border-edge bg-white p-2 lg:p-1 lg:pb-8"
          >
            {/* QR Code - Desktop */}
            <div className="hidden lg:inline-block pt-6 pb-10">
              <div className="relative inline-block">
                <QRCodeSVG
                  value={url}
                  size={300}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="M"
                  includeMargin={false}
                />
                {label && (
                  <div
                    className="absolute bottom-[-55px] left-0 right-0 rounded-b-lg shadow-md p-3"
                    style={{
                      backgroundColor: brandColors['accent-50'] || primaryColor,
                      color: setContrast(brandColors['accent-50'] || primaryColor),
                    }}
                  >
                    <p
                      className="break-words text-2xl font-semibold text-center"
                      style={{
                        color: setContrast(brandColors['accent-50'] || primaryColor),
                      }}
                    >
                      {label}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* QR Code - Mobile */}
            <div className="inline-block lg:hidden">
              <div className="relative inline-block pt-6 pb-10">
                <QRCodeSVG
                  value={url}
                  size={190}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="M"
                  includeMargin={false}
                />
                {label && (
                  <div
                    className="absolute bottom-[10.5px] left-0 right-0 rounded-b-lg shadow-md p-1"
                    style={{
                      backgroundColor: brandColors['accent-50'] || primaryColor,
                      color: setContrast(brandColors['accent-50'] || primaryColor),
                    }}
                  >
                    <p
                      className="break-words text-sm font-semibold text-center"
                      style={{
                        color: setContrast(brandColors['accent-50'] || primaryColor),
                      }}
                    >
                      {label}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Label under QR Code */}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end gap-3">
            <Button
              variant="neutral"
              size="workspace"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2"
            >
              Close
            </Button>
            <Button
              variant="neutral"
              size="workspace"
              onClick={copyToClipboard}
              disabled={isCopying}
              className="flex items-center gap-2"
            >
              <svg
                width="18"
                height="19"
                viewBox="0 0 18 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.875 3.19409C7.36058 3.19409 6.92546 3.53984 6.79207 4.01271C6.76478 4.10947 6.75 4.21209 6.75 4.31909H11.25C11.25 4.21209 11.2352 4.10947 11.2079 4.01271C11.0745 3.53984 10.6394 3.19409 10.125 3.19409H7.875ZM5.8549 3.32751C6.22133 2.58236 6.98769 2.06909 7.875 2.06909H10.125C11.0123 2.06909 11.7787 2.58236 12.1451 3.32751C12.5183 3.35931 12.8899 3.39672 13.2598 3.43965C14.3829 3.56998 15.1875 4.53451 15.1875 5.63683V15.5691C15.1875 16.8117 14.1801 17.8191 12.9375 17.8191H5.0625C3.81986 17.8191 2.8125 16.8117 2.8125 15.5691V5.63683C2.8125 4.53451 3.61712 3.56998 4.74017 3.43965C5.1101 3.39672 5.48171 3.35931 5.8549 3.32751Z"
                  fill="currentColor"
                />
              </svg>
              {isCopying ? 'Copying...' : 'Copy Image'}
            </Button>
            <Button
              onClick={downloadImage}
              disabled={isDownloading}
              variant="neutral"
              size="workspace"
            >
              {isDownloading ? 'Downloading...' : 'Download'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRCodeModal;
